import AriaControl from "../../../utilities/accessibility/js/AriaControl";
import { _defaultConfigOptions } from "../../../utilities/accessibility/js/constants";
import SearchToggle from "./SearchToggle";
/**
 * MainNavigationControl is used to initialise a navigation control
 * that looks for the control element as well as any elements it should toggle
 * Indicated by the data attributes:
 *  [data-control='id']
 *  [data-controlled-by='id|id']
 *
 * 'data-controlled-by' being a pipe separated value of ids, so that many controls
 * can control the same component.
 *
 * We also consider the case where our containing menu should remain open while
 * we toggle content, so have a tertiary check for:
 *  [data-control-menu]
 *
 * An optional parameter isClose is used to determine if the button should function
 * as just a close button.
 */
export default class SearchToggleControl extends AriaControl {
  /**
   * Default config that we control the ARIA attributes with
   */
  static defaultConfigOptions = {
    ..._defaultConfigOptions,
    menu: undefined,
    controls: undefined,
  };

  /**
   * Construct the control and set properties from the config
   * Extended configO
   *
   * @param {Object} configOptions
   */
  constructor(configOptions) {
    // Get the required options to construct control
    const {
      element,
      page,
      root,
      menu,
      controls,
      allTargets,
      expandable,
      expanded,
    } = configOptions;
    // AriaControl will handle initial ARIA configuration
    super({ control: element, expandable, expanded });
    this.configOptions = configOptions;
    // Control configuration
    this.control = element;
    this.page = page;
    this.root = root;
    this.menu = menu;
    this.controls = controls;
    this.allTargets = allTargets;
    // Determine if this button is a close button
    this.id = this.control.id;
    this.classList = this.control.classList;
    this.isClose = /close/gi.exec(this.id);
    this.targets = this.menu.querySelectorAll(
      `[data-controlled-by*=${this.id}]`,
    );
    // Add event listeners
    this.control.addEventListener("click", this.handleClick.bind(this));
  }
  /**
   * Toggle the provided element
   * Expanded state can be specified manually
   *
   * @param {defaultConfigOptions} configOptions
   */
  toggleElement(
    element,
    expanded = undefined,
    modifyAria = false,
    focusInputs = false,
  ) {
    // Get static variables
    const activeClass = SearchToggle.activeClass;
    const lockedClass = SearchToggle.lockedClass;

    // Switch statement because we also want to have a default behaviour
    switch (!!expanded) {
      case true:
        element.classList.add(activeClass);
        this.root.classList.add(activeClass);
        setTimeout(() => {
          // Hack to circumvent Menu toggle
          this.page.classList.add(lockedClass);
          SearchToggle.focusTrap.activate();
        }, 10);
        break;
      default:
        element.classList.remove(activeClass);
        this.root.classList.remove(activeClass);
        this.page.classList.remove(lockedClass);
        SearchToggle.focusTrap.deactivate();
    }

    if (modifyAria) {
      // Modify the ARIA attributes alongside the element
      this.modifyAria({
        ...this.configOptions,
        target: element,
        expanded,
        action: "expand",
      });

      // focus form inputs
      if (focusInputs) this.focusInputs(expanded);
    }
  }

  focusInputs(expanded) {
    // focus the input
    setTimeout(() => {
      if (this.allTargets.length) {
        const targets = Object.entries(this.allTargets);
        const searchInputs = targets.filter((f) => {
          return f[1].classList.contains("form__search-input");
        });
        if (searchInputs.length) {
          searchInputs.forEach((elm) => {
            expanded ? elm[1].focus() : elm[1].blur();
          });
        }
      }
    }, 0);
  }
  /**
   * Dedicated menu handler
   * Toggles just the menu element
   *
   * @param {Boolean} expanded
   */
  toggleMenu(expanded = undefined) {
    // Toggle (and modifyAria)
    this.toggleElement(this.menu, expanded, true, true);
  }

  /**
   * The function of this click is fairly simple, we only want to
   * toggle the '.active' class, or if the current active menu item
   * isn't ours, display the relevant menu without hiding the main menu
   */
  handleClick() {
    // Get static properties
    const activeTarget = SearchToggle.activeTarget;
    const setActiveTarget = SearchToggle.setActiveTarget;

    if (!activeTarget || !this.isClose) {
      // Menu isn't open, toggle active
      this.toggleMenu(true);
      setActiveTarget(this.id);
    } else {
      // We're closing the menu
      this.toggleMenu(false);
      setActiveTarget("");
    }
  }
}
